import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { Upload, CheckCircle, Clock, XCircle, FileText, AlertCircle, Download } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

const DOC_TYPES = [
  { key: 'registration_certificate', label: 'Registration Certificate', required: true, hint: 'Certificate of Incorporation / Society / Trust registration' },
  { key: 'pan_card', label: 'PAN Card', required: true, hint: 'Organisation PAN card copy' },
  { key: 'gst_certificate', label: 'GST Certificate', required: false, hint: 'GST registration certificate (if applicable)' },
  { key: 'certificate_12a_80g', label: '12A / 80G Certificate', required: false, hint: 'For NGOs / non-profits (if applicable)' },
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export default function MemberDocuments() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputs = useRef({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchApplication();
  }, [user, authLoading, navigate]);

  const fetchApplication = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/membership-applications/my-application`, {
        headers: authHeaders(), withCredentials: true
      });
      setApplication(data);
    } catch (error) {
      if (error.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append('doc_type', docType);
      formData.append('file', file);
      await axios.post(`${API_BASE_URL}/api/membership-applications/upload-document`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      await fetchApplication();
    } catch (error) {
      alert(error.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(null);
    }
  };

  const getDocForType = (docType) => (application?.documents || []).find(d => d.doc_type === docType);

  const statusBadge = (status) => {
    if (status === 'approved') return <Badge className="bg-green-100 text-green-800" data-testid={`doc-status-approved`}><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
    if (status === 'rejected') return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending Review</Badge>;
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/membership-applications/documents/${doc.id}/download`,
        { headers: authHeaders(), withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_name;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
    } catch (e) {
      alert('Download failed');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow flex items-center justify-center pt-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722]"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F]/90 pt-32 pb-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold text-white" data-testid="member-documents-title">Verification Documents</h1>
          <p className="text-gray-200 mt-2">Upload documents to complete your institutional membership verification</p>
        </div>
      </div>

      <main className="flex-grow">
        <div className="container mx-auto px-4 py-10 max-w-4xl">
          {notFound ? (
            <Card className="p-10 text-center" data-testid="no-application-card">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-2">No Membership Application Found</h2>
              <p className="text-gray-600 mb-6">
                We couldn't find a membership application linked to <strong>{user?.email}</strong>.
                Apply for membership first, then return here to upload your documents.
              </p>
              <Button onClick={() => navigate('/membership')} className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid="apply-membership-btn">
                View Membership Plans
              </Button>
            </Card>
          ) : application?.member_type !== 'institutional' ? (
            <Card className="p-10 text-center" data-testid="individual-no-docs-card">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-2">No Documents Required</h2>
              <p className="text-gray-600">
                Document verification is only required for institutional members.
                Your individual membership does not need any document uploads.
              </p>
            </Card>
          ) : (
            <>
              {/* Status Banner */}
              <Card className="p-6 mb-8" data-testid="verification-status-banner">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Organisation</p>
                    <p className="font-bold text-lg text-[#1E3A5F]">{application.organization_name || application.name}</p>
                    <p className="text-sm text-gray-500">{application.selected_plan}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 mb-1">Application Status</p>
                    {application.status === 'approved' ? (
                      <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Verified Member</Badge>
                    ) : application.status === 'rejected' ? (
                      <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Under Review</Badge>
                    )}
                  </div>
                </div>
                {application.review_notes && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                    <strong>Admin note:</strong> {application.review_notes}
                  </div>
                )}
              </Card>

              {/* Document Slots */}
              <div className="space-y-4">
                {DOC_TYPES.map((dt) => {
                  const doc = getDocForType(dt.key);
                  return (
                    <Card key={dt.key} className="p-6" data-testid={`doc-slot-${dt.key}`}>
                      <div className="flex items-start justify-between flex-wrap gap-4">
                        <div className="flex items-start gap-3">
                          <FileText className="h-8 w-8 text-[#E87722] flex-shrink-0 mt-1" />
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-bold text-[#1E3A5F]">{dt.label}</h3>
                              {dt.required ? (
                                <Badge variant="outline" className="text-xs border-red-300 text-red-600">Required</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">Optional</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{dt.hint}</p>
                            {doc && (
                              <div className="flex items-center gap-3 mt-3 flex-wrap">
                                {statusBadge(doc.status)}
                                <button
                                  onClick={() => handleDownload(doc)}
                                  className="text-sm text-[#1E3A5F] underline flex items-center gap-1 hover:text-[#E87722]"
                                  data-testid={`doc-download-${dt.key}`}
                                >
                                  <Download className="h-3 w-3" />{doc.original_name}
                                </button>
                              </div>
                            )}
                            {doc?.status === 'rejected' && doc.admin_notes && (
                              <p className="text-sm text-red-600 mt-2">Reason: {doc.admin_notes}</p>
                            )}
                          </div>
                        </div>

                        <div>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            ref={(el) => (fileInputs.current[dt.key] = el)}
                            onChange={(e) => handleFileSelect(dt.key, e.target.files[0])}
                            data-testid={`doc-file-input-${dt.key}`}
                          />
                          <Button
                            variant={doc ? 'outline' : 'default'}
                            className={doc ? '' : 'bg-[#E87722] hover:bg-[#d66a1a]'}
                            disabled={uploading === dt.key}
                            onClick={() => fileInputs.current[dt.key]?.click()}
                            data-testid={`doc-upload-btn-${dt.key}`}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            {uploading === dt.key ? 'Uploading...' : doc ? 'Replace' : 'Upload'}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              <p className="text-xs text-gray-500 mt-6 text-center">
                Accepted formats: PDF, JPG, PNG • Max size 5MB per file. Our team reviews documents within 2-3 working days.
              </p>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
